import { CommonModule,getCurrencySymbol } from '@angular/common';
import { Component,DEFAULT_CURRENCY_CODE,Inject,OnDestroy,Optional } from '@angular/core';
import { FormControl,FormGroup,FormsModule,ReactiveFormsModule } from '@angular/forms';
import { MatLegacyDialogConfig as MatDialogConfig,MatLegacyDialogModule as MatDialogModule,MatLegacyDialogRef as MatDialogRef,MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacySlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { Actions,ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject,firstValueFrom,Observable,of,switchMap } from 'rxjs';
import { map,shareReplay } from 'rxjs/operators';
import {
    _calcAccountsReceivableLedgerAmountWithAllowances,
    AccountsReceivableLedgerEntity, AccountsReceivableLedgerTypeEnum,
    financialRound,
} from '../../../dave-data-module/entities/accounts-receivable-ledger.entity';
import {
    LedgerImportARLCalculationPreChargeByType,
    LedgerImportDocumentTypeNames, LedgerImportDocumentTypes, LedgerImportEntity,
} from '../../../dave-data-module/entities/ledger-import.entity';
import { State } from '../../../dave-data-module/State';
import { AccountsReceivableLedgerActionTypes,LedgerImportActionTypes } from '../../../dave-data-module/State/actions/accounting.actions';
import { BaseActionTypes } from '../../../dave-data-module/State/actions/base.actions';
import {
    getLedgerImportById,
    getLedgerImports,
    getLedgerImportsFetched,
} from '../../../dave-data-module/State/selectors/ledger-import.selector';
import { AppButtonModule } from '../../../dave-utils-module/app-button-module/app-button.module';
import { appMatDialogDefaultConfig,isNotNullOrUndefined,MathRound } from '../../../helper/helper';
import { HintIconComponent } from '../../../hint-icon/hint-icon.component';
import { LoadingService } from '../../../services/loading.service';
import {
    LedgerImportSelectTableComponent,
    LedgerImportSelectTableComponentLedgerImportFilter, LedgerImportSelectTableFilterValues,
} from '../ledger-import-select-table/ledger-import-select-table.component';
import { getFetched$ } from '../../../dave-data-module/helper/helper';
import { SelectArlDialogService } from '../select-arl-dialog/select-arl-dialog.service';
import { Dictionary } from '@ngrx/entity';
import Big from 'big.js';
import {
    _getArlChildren,
    getARLCostAmountWithAllowance,
    getParentsBaseCostAllowanceCharges,
} from '../arl-calculation/arl-calculation.helper';
import { ArlDataService } from '../../../dave-data-module/services/arl-data.service';
import { ArlInputBuilderService } from '../../../dave-data-module/services/arl-input-builder.service';

export interface NewPartialInvoiceComponentDialogData {
    FilterLedgerImports?: {
        CommissionId?: number;
        CustomerId?: number;
    };
}
export type NewPartialInvoiceComponentDialogReturnData =
    | undefined
    | {
          LedgerImportCreated: number;
      };
@Component({
    selector: 'app-new-partial-invoice',
    standalone: true,
    imports: [CommonModule, MatDialogModule, LedgerImportSelectTableComponent, AppButtonModule, MatFormFieldModule, MatInputModule, FormsModule, ReactiveFormsModule, MatLegacySlideToggleModule, HintIconComponent],
    templateUrl: './new-partial-invoice.component.html',
    styleUrls: ['./new-partial-invoice.component.scss'],
})
export class NewPartialInvoiceComponent {
    public static DefaultConfig: MatDialogConfig = {
        ...appMatDialogDefaultConfig,
        minWidth: '60vw',
    };
    title = LedgerImportDocumentTypeNames.get(LedgerImportDocumentTypes.PartialInvoice) + ' erstellen';
    selectedLedgerImportId$ = new BehaviorSubject<number | null>(null);
    selectedLedgerImport$ = this.selectedLedgerImportId$.pipe(switchMap((id) => (id ? this.store.select(getLedgerImportById({ id })).pipe(shareReplay({ refCount: true, bufferSize: 1 })) : of(null))));
    currencyCode$ = this.selectedLedgerImport$.pipe(map((li) => getCurrencySymbol(li?.ConsolidatedCurrencyCode || this.defaultCurrencyCode, 'narrow')));
    amountForm = new FormGroup({
        decimal: new FormControl<number>(null),
        usePercent: new FormControl<boolean>(true),
        percent: new FormControl<number>(null),
    });
    isKumulativ = new FormControl<boolean>(false);
    setAmount = new FormControl<boolean>(false);
    selectableSrcLedgerImports$: Observable<number[]>;
    selectablePreviousLedgerImports$: Observable<number[]>;
    selectedLedgerImports: number[] = [];

    readonly selectableSrcLedgerImportsFilter: LedgerImportSelectTableComponentLedgerImportFilter;
    readonly selectablePrevLedgerImportsFilter: LedgerImportSelectTableComponentLedgerImportFilter;

    readonly selectableSrcLedgerImportsDocumentTypesDefaultFilter = [
        LedgerImportDocumentTypes.Invoice,
        LedgerImportDocumentTypes.Offer,
        LedgerImportDocumentTypes.OfferConfirmation,
        LedgerImportDocumentTypes.PartialInvoice,
        LedgerImportDocumentTypes.Measurement,
    ];
    readonly selectablePrevLedgerImportsDocumentTypesDefaultFilter = [
        LedgerImportDocumentTypes.Invoice,
        LedgerImportDocumentTypes.PartialInvoice,
        LedgerImportDocumentTypes.Cancellation,
        LedgerImportDocumentTypes.Credit,
    ];

    constructor(
        @Optional() @Inject(MAT_DIALOG_DATA) dialogData: NewPartialInvoiceComponentDialogData,
        private dialogRef: MatDialogRef<NewPartialInvoiceComponent, NewPartialInvoiceComponentDialogReturnData>,
        private store: Store<State>,
        protected ls: LoadingService,
        private actions$: Actions,
        @Inject(DEFAULT_CURRENCY_CODE) private defaultCurrencyCode: string,
        private selectArlDialogService: SelectArlDialogService,
        private arlDataService: ArlDataService,
        private arlInputBuilder: ArlInputBuilderService
    ) {
        this.selectableSrcLedgerImportsFilter = {
            customerIds: dialogData?.FilterLedgerImports?.CustomerId ? [dialogData.FilterLedgerImports.CustomerId] : [],
            commissionIds: dialogData?.FilterLedgerImports?.CommissionId ? [dialogData.FilterLedgerImports.CommissionId] : [],
            documentTypes: [],
        };
        this.selectablePrevLedgerImportsFilter = {
            ...this.selectableSrcLedgerImportsFilter,
            documentTypes: [LedgerImportDocumentTypes.Invoice, LedgerImportDocumentTypes.PartialInvoice, LedgerImportDocumentTypes.Cancellation, LedgerImportDocumentTypes.Credit],
        }
        const filterByDialogData = (v: LedgerImportEntity[]) => {
            let ret = v;
            if (dialogData?.FilterLedgerImports?.CommissionId) {
                ret = ret.filter((li) => li.CommissionId === dialogData.FilterLedgerImports.CommissionId);
            }
            if (dialogData?.FilterLedgerImports?.CustomerId) {
                ret = ret.filter((li) => li.CustomerId === dialogData.FilterLedgerImports.CustomerId);
            }
            return ret;
        };
        this.selectablePreviousLedgerImports$ = getFetched$(store, getLedgerImportsFetched, getLedgerImports).pipe(
            map((lis) =>
                filterByDialogData(lis).filter((l) => [LedgerImportDocumentTypes.Invoice, LedgerImportDocumentTypes.PartialInvoice, LedgerImportDocumentTypes.Cancellation, LedgerImportDocumentTypes.Credit].includes(l.DocumentType)),
            ),
            map((lis) => lis.map((l) => l.Id)),
        );
        // this.selectableSrcLedgerImports$ = this.store.select(getLedgerImports).pipe(
        //     // switchMap((lis) =>
        //     //     this.isKumulativ.valueChanges.pipe(
        //     //         startWith(null),
        //     //         map(() => this.isKumulativ.value),
        //     //         map((isKumulativ) => (isKumulativ ? lis : lis.filter((l) => l.DocumentType === LedgerImportDocumentTypes.Offer || l.DocumentType === LedgerImportDocumentTypes.OfferConfirmation))),
        //     //     ),
        //     // ),
        //     map((lis) => {
        //         return filterByDialogData(lis);
        //     }),
        //     map((lis) => lis.map((l) => l.Id)),
        // );
        // firstValueFrom(this.selectablePreviousLedgerImports$).then((lis) => {
        //     this.selectedLedgerImports = lis; todo defaultmäßig alle auswählen
        // })
    }
    setPercent(value: number) {
        if (this.selectedLedgerImportId$.value) {
            firstValueFrom(this.selectedLedgerImport$).then((li) => {
                this.amountForm.patchValue({
                    usePercent: true,
                    decimal: MathRound(((value || 0) / 100) * ((li.ConsolidatedNettoAmount || 0) / 100), true),
                    percent: value,
                });
            });
        } else {
            this.amountForm.patchValue({
                usePercent: true,
                percent: value,
            });
        }
    }
    setDecimal(value: number) {
        if (this.selectedLedgerImportId$.value) {
            firstValueFrom(this.selectedLedgerImport$).then((li) => {
                this.amountForm.patchValue({
                    usePercent: false,
                    percent: li.ConsolidatedNettoAmount ? MathRound(((value || 0) / (li.ConsolidatedNettoAmount / 100)) * 100, true) : 0,
                    decimal: value,
                });
            });
        } else {
            this.amountForm.patchValue({
                usePercent: false,
                decimal: value,
            });
        }
    }
    private async selectArls(ledgerImportId: number) {
        const dialogRef = await this.selectArlDialogService.open({
            AllowQuantityManipulation: false,
            AllowQuantityTypeManipulation: false,
            AllowPriceManipulation: false,
            AllowBezeichnungManipulation: false,
            LiId: ledgerImportId,
            // LedgerImportDocumentType: this.Data.newOptionDefaultValues.LedgerImportDocumentType,
            showForceItemNumbers: true,
            forceItemNumbersDefaultOn: true,
            disableForceItemNumbers: true,
        });
        const response = await firstValueFrom(dialogRef.afterClosed());
        if (response) {
            return response.ArlOutput.map(a => a.Id);
        } else {
            return null;
        }
    }




    async submit() {
        if (this.selectedLedgerImportId$.value) {
            let selectedArls: number[];
            let arls: { id: number; baseCost: number; lohnBaseCostPart: number; costAmount: number; baseCostGross: number; }[] | undefined;
            if (!this.setAmount.value) {
                selectedArls = await this.selectArls(this.selectedLedgerImportId$.value);
                if (!selectedArls) {
                    return;
                }
                const arlDictionary = await firstValueFrom(this.arlDataService.selectAllArlsDict());



                const WithoutCalculation = [
                    ...new Set(
                        selectedArls.flatMap(id =>
                            _getArlChildren(id, arlDictionary, true)
                        )
                    ),
                ];

                const WithCalculation = [
                    ...new Set(
                        selectedArls.flatMap(id =>
                            _getArlChildren(id, arlDictionary, false)
                        )
                    ),
                ];

                const calculationArlIds = WithCalculation
                    .filter(a => !WithoutCalculation.some(w => w.Id === a.Id))
                    .map(a => a.Id);


                selectedArls = [
                    ...new Set([
                        ...selectedArls,
                        ...calculationArlIds
                    ])
                ];


                const rootLedgerImport = await firstValueFrom(this.store.select(getLedgerImportById({ id: this.selectedLedgerImportId$.value })));
                arls = this.arlInputBuilder.buildArlInputs(selectedArls, arlDictionary, rootLedgerImport?.ARLCalculationPreChargeByType || []);


            }

            console.log('### selectedArls', selectedArls);
            this.ls.startLoading('app-new-partial-invoice-submit');
            firstValueFrom(
                this.actions$.pipe(
                    ofType(
                        LedgerImportActionTypes.CreatePartialInvoiceSuccess,
                        LedgerImportActionTypes.CreatePartialInvoiceFailure,
                        LedgerImportActionTypes.CreateCumulativePartialInvoiceSuccess,
                        LedgerImportActionTypes.CreateCumulativePartialInvoiceFailure,
                    ),
                ),
            ).then((action) => {
                if (action.type === LedgerImportActionTypes.CreatePartialInvoiceSuccess.type || action.type === LedgerImportActionTypes.CreateCumulativePartialInvoiceSuccess.type) {
                    this.ls.endLoading('app-new-partial-invoice-submit');
                    const { Id } = action.Payload as LedgerImportEntity;
                    this.dialogRef.close({
                        LedgerImportCreated: Id,
                    });
                } else {
                    this.ls.endLoading('app-new-partial-invoice-submit');
                }
            });
            if (this.isKumulativ.value) {
                this.store.dispatch(
                    LedgerImportActionTypes.CreateCumulativePartialInvoiceRequest({
                        Payload: {
                            rootId: this.selectedLedgerImportId$.value,
                            ledgerImportIds: this.selectedLedgerImports,
                            amount: this.setAmount.value ? (isNotNullOrUndefined(this.amountForm.value.decimal) ? financialRound(this.amountForm.value.decimal * 100, 0) : 0) : null,
                            percentageAmountInformation: this.setAmount.value ? (this.amountForm.value.percent || 0) : null,
                            arls: arls
                        },
                    }),
                );
            } else {
                this.store.dispatch(
                    LedgerImportActionTypes.CreatePartialInvoiceRequest({
                        Payload: {
                            rootId: this.selectedLedgerImportId$.value,
                            amount: this.setAmount.value ? (isNotNullOrUndefined(this.amountForm.value.decimal) ? financialRound(this.amountForm.value.decimal * 100, 0) : 0) : null,
                            percentageAmountInformation: this.setAmount.value ? (this.amountForm.value.percent || 0) : null,
                            arls: arls
                        },
                    }),
                );
            }
        }
    }

    selectedLedgerImportChange(id: number) {
        if (this.selectedLedgerImportId$.value !== id) {
            this.selectedLedgerImportId$.next(id);
            if (this.amountForm.value.usePercent) {
                if (isNotNullOrUndefined(this.amountForm.value.percent)) {
                    this.setPercent(this.amountForm.value.percent);
                }
            } else {
                if (isNotNullOrUndefined(this.amountForm.value.decimal)) {
                    this.setDecimal(this.amountForm.value.decimal);
                }
            }
        }
    }
}
